<script setup lang="ts">
import {type Questionnaire, QuestionnaireType} from '@/ts/types/dto/interventions-dto';
import {
  computed,
  ref,
  watch,
} from 'vue';
import {
  AnswerComponentType,
  type QuestionnaireAnswers,
  type SessionRequestQuestionnaire,
} from '@/ts/types/dto/session-request-dto';
import QuestionComponent from '@/vue/molecules/components/question/question-component.vue';
import {useLogger} from '@/ts/composables/pure/use-logger';
import ContentBlock from '@/vue/molecules/components/helpers/content-block.vue';
import {ComponentTheme} from '@/ts/types/component/layout-component-types';

const props = defineProps<{
  questionnaire: Questionnaire;
}>();
const logger = useLogger();
const model = defineModel<SessionRequestQuestionnaire[] | null>();
const QUESTIONNAIRE_ONLY_QUESTIONS_CLASS = 'questionnaire-only-questions';

const questionsClass = computed(() => {
  if (props.questionnaire.type === QuestionnaireType.GRID_LAYOUT) {
    return 'questionnaire-grid';
  }
  if (props.questionnaire.type === QuestionnaireType.SHOW_ONLY_QUESTIONS) {
    return QUESTIONNAIRE_ONLY_QUESTIONS_CLASS;
  }
  if (props.questionnaire.type === QuestionnaireType.WEIGHT_LOSS) {
    return QUESTIONNAIRE_ONLY_QUESTIONS_CLASS;
  }
  if (props.questionnaire.type === QuestionnaireType.WEIGHT_LOSS_PERCENT) {
    return QUESTIONNAIRE_ONLY_QUESTIONS_CLASS;
  }
  logger.warn(`Unknown questionnaire type "${props.questionnaire.type}", using default class`)();
  return QUESTIONNAIRE_ONLY_QUESTIONS_CLASS;
});

// this should be a separate value as v-model=model[i] doesn't execute computed setter
const value = ref<QuestionnaireAnswers[]>([]);

watch(
  value,
  (newValue) => {
    logger.debug(`watch ${props.questionnaire.id}`, newValue)();
    model.value = newValue.map(((nv: QuestionnaireAnswers) => ({
      type: AnswerComponentType.QUESTIONNAIRE,
      questionnaireAnswers: nv,
    })));
  },
  {deep: true},
);

</script>
<template>
  <content-block
    v-if="questionnaire.image || questionnaire.description || questionnaire.name"
    :content="questionnaire.description"
    :image="questionnaire.image"
    class="questionnaire-grid-cont"
    :name="questionnaire.name"
    :theme="ComponentTheme.CARD"
  />
  <div class="questionnaire-grid-vert" :class="questionsClass">
    <question-component
      v-for="(question, i) in questionnaire.questions"
      :key="question.contentId"
      v-model="value[i]"
      :questionnaire-id="props.questionnaire.id"
      :questionnaire-type="props.questionnaire.type"
      :question="question"
    />
  </div>
</template>
<style scoped lang="scss">

.questionnaire-grid-cont {
  margin-bottom: var(--Spacing-spacing-7);
}

.questionnaire-grid {
  gap: var(--Spacing-spacing-4);
  display: grid;
  grid-template-columns: 1fr;

  @include screen-sm {
    grid-template-columns: repeat(2, 1fr);
  }

  @include screen-xl {
    grid-template-columns: repeat(3, 1fr);
  }
}

.questionnaire-grid-vert {
   --btn-background-on-card: var(--Neutral-UI-neutral-ui-10);
}

.questionnaire-only-questions {
  display: flex;
  gap: var(--Spacing-spacing-8);
  flex-direction: column;
}

</style>
